/**
 * Versions and releases (plan.md step 54).
 *
 * Here because both sides speak it: the phone shell WRITES its version into
 * the web view's user agent and the web app READS it for the version matrix.
 * Two copies of the format would drift, and the matrix would then report a
 * phone app it does not recognise as a browser.
 */

export interface NativeApp {
  os: string;
  /** The store version — `app.config.ts`, which reads the platform version. */
  version: string | null;
  /** EAS's build number; absent in Expo Go and on a web build. */
  build: string | null;
  /**
   * The shell lets its web view use passkeys (the iPhone app from 2026-10-29, which carries the
   * `webcredentials:` entitlement for the platform domain), or the ANDROID app that carries the passkey
   * bridge (`mobile/modules/passkeys`, 2026-10-30): Android's web view has no WebAuthn, so there the page
   * asks the shell (`passkey.get` / `passkey.create`), which asks Android's Credential Manager. An older
   * build does not say so, and the page then never offers a passkey there.
   */
  passkeys: boolean;
  /**
   * The shell signs in with Google natively (the iPhone app from 2026-10-30 — the user: Google's quick sign-in
   * in the app): "Continue with Google" asks it (`google.signin`) for Google's own account sheet instead of a
   * browser page. An older build does not say so, and the page keeps the browser sign-in there.
   */
  google: boolean;
  /** The shell signs in with Apple natively (the iPhone app, 2026-10-30): one tap and Face ID (`apple.signin`). */
  apple: boolean;
}

/**
 * `MyRaveApp/ios/0.1.0 (37)` — appended to the web view's user agent; ` passkeys`, ` google` and ` apple` after it,
 * in that order, when the shell can.
 */
export function nativeAppAgent(os: string, version: string, build: string | null, opts: { passkeys?: boolean; google?: boolean; apple?: boolean } = {}): string {
  return `MyRaveApp/${os}/${version}${build ? ` (${build})` : ''}${opts.passkeys ? ' passkeys' : ''}${opts.google ? ' google' : ''}${opts.apple ? ' apple' : ''}`;
}

/**
 * The phone app this page runs inside, or null in a browser. A shell from
 * before step 54 sent only `MyRaveApp/<os>` — still the app, version unknown.
 */
export function readNativeApp(userAgent: string): NativeApp | null {
  const m = /MyRaveApp\/([a-z]+)(?:\/([0-9A-Za-z.+-]+)(?: \(([^)]+)\))?)?( passkeys\b)?( google\b)?( apple\b)?/.exec(userAgent);
  if (!m) return null;
  return { os: m[1]!, version: m[2] ?? null, build: m[3] ?? null, passkeys: Boolean(m[4]), google: Boolean(m[5]), apple: Boolean(m[6]) };
}

/**
 * One version per module in the catalogue — the key the `modules.module` rows
 * carry (plan.md step 54). Bump a module's line in the release that changes it:
 * MAJOR breaks something (an API, a stored shape), MINOR adds, PATCH fixes.
 * `tests/src/moduleVersions.test.ts` holds this to exactly the catalogue the
 * migrations make, and `tests/src/releaseHistory.test.ts` to the history: each
 * module's version is the newest `releases/*.md` that names it (derived from
 * the git log on 2026-10-06, 267 commits).
 */
export const MODULE_VERSIONS: Readonly<Record<string, string>> = {
  lineup: '0.14.86',
  schedule: '0.14.85',
  search: '0.14.83',
  'my-lineup': '0.14.84',
  live: '0.14.86',
  map: '0.14.83',
  countdown: '0.14.38',
  info: '0.14.86',
  announcements: '0.14.67',
  sponsors: '0.14.55',
  notifications: '0.14.85',
  offline: '0.14.83',
  travel: '0.14.83',
  dating: '0.14.71',
  photos: '0.14.70',
  'crowd-stats': '0.3.0',
  'ai-assistant': '0.14.83',
  recordings: '0.14.83',
  // Step 65: no release names it yet — the next cut (`release.mts next`) sets it, as for every module it touches.
  weather: '0.14.86',
};

/* ------------------------------------------------------------ release notes */

export const RELEASE_KINDS = ['FEATURE', 'FIX', 'PATCH', 'SECURITY', 'CONTENT'] as const;
export type ReleaseKind = (typeof RELEASE_KINDS)[number];

/** The sections a `releases/<version>.md` may have, in the order they read. */
export const RELEASE_SECTIONS = ['NEW', 'IMPROVED', 'FIXED', 'SECURITY', 'BREAKING', 'KNOWN ISSUES'] as const;

export interface ReleaseNotes {
  version: string;
  kind: ReleaseKind;
  scope: 'GLOBAL' | 'FESTIVAL';
  festivalId: string | null;
  /** `YYYY-MM-DD`, when it was released; null when the file does not say. */
  date: string | null;
  /** The modules this release changes — each bumped in `MODULE_VERSIONS`. */
  modules: string[];
  notes: Partial<Record<(typeof RELEASE_SECTIONS)[number], string[]>>;
  /**
   * Every line, in order, with the modules it names: `- [map, offline] …`.
   * This is what gives each module its own history in the version matrix.
   */
  items: { section: (typeof RELEASE_SECTIONS)[number]; text: string; modules: string[] }[];
}

const SEMVER = /^\d+\.\d+\.\d+$/;

/** Negative, zero or positive, as `a` is below, equal to or above `b` — by number, not text. */
export function compareSemver(a: string, b: string): number {
  const [x, y] = [a, b].map((v) => v.split('.').map(Number)) as [number[], number[]];
  for (let i = 0; i < 3; i++) if (x[i] !== y[i]) return x[i]! - y[i]!;
  return 0;
}

/**
 * Read one `releases/<version>.md`:
 *
 *     ---
 *     version: 0.2.0
 *     kind: FEATURE            (FEATURE | FIX | PATCH | SECURITY | CONTENT)
 *     scope: GLOBAL            (or FESTIVAL, with `festival: <id>`)
 *     modules: [lineup, map]
 *     ---
 *     ## NEW
 *     - one line per change
 *
 * Throws, naming the problem, rather than reading a typo as an empty section:
 * the notes are what the platform's "What's new" page shows people.
 */
export function parseReleaseNotes(text: string): ReleaseNotes {
  const m = /^---\n([\s\S]*?)\n---\n?([\s\S]*)$/.exec(text.replace(/\r\n/g, '\n'));
  if (!m) throw new Error('release notes must open with a --- frontmatter block');
  const front = new Map<string, string>();
  for (const line of m[1]!.split('\n')) {
    const kv = /^([a-z]+):\s*(.*)$/.exec(line.trim());
    if (kv) front.set(kv[1]!, kv[2]!.trim());
  }
  const version = front.get('version') ?? '';
  if (!SEMVER.test(version)) throw new Error(`release notes need "version: MAJOR.MINOR.PATCH" (got "${version}")`);
  const kind = front.get('kind') ?? '';
  if (!(RELEASE_KINDS as readonly string[]).includes(kind)) throw new Error(`release kind must be one of ${RELEASE_KINDS.join(', ')} (got "${kind}")`);
  const scope = front.get('scope') ?? 'GLOBAL';
  if (scope !== 'GLOBAL' && scope !== 'FESTIVAL') throw new Error(`release scope must be GLOBAL or FESTIVAL (got "${scope}")`);
  const festivalId = front.get('festival') || null;
  if ((scope === 'FESTIVAL') !== (festivalId !== null)) throw new Error('a FESTIVAL release names its festival, and only it does');
  const date = front.get('date') || null;
  if (date !== null && !/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error(`release date must be YYYY-MM-DD (got "${date}")`);
  const modules = (front.get('modules') ?? '').replace(/^\[|\]$/g, '').split(',').map((s) => s.trim()).filter(Boolean);
  for (const key of modules) if (!(key in MODULE_VERSIONS)) throw new Error(`"${key}" is not a module in the catalogue`);

  const notes: ReleaseNotes['notes'] = {};
  const items: ReleaseNotes['items'] = [];
  let section: (typeof RELEASE_SECTIONS)[number] | null = null;
  for (const raw of m[2]!.split('\n')) {
    const line = raw.trim();
    const heading = /^##\s+(.+)$/.exec(line);
    if (heading) {
      const name = heading[1]!.trim().toUpperCase();
      if (!(RELEASE_SECTIONS as readonly string[]).includes(name)) throw new Error(`unknown section "## ${heading[1]}" — use ${RELEASE_SECTIONS.join(', ')}`);
      section = name as (typeof RELEASE_SECTIONS)[number];
      continue;
    }
    const item = /^[-*]\s+(.+)$/.exec(line);
    if (!item || !section) continue;
    const tagged = /^\[([a-z0-9, -]+)\]\s+(.+)$/.exec(item[1]!);
    const lineModules = tagged ? tagged[1]!.split(',').map((s) => s.trim()).filter(Boolean) : [];
    for (const key of lineModules) {
      if (!(key in MODULE_VERSIONS)) throw new Error(`"${key}" is not a module in the catalogue`);
      if (!modules.includes(key)) throw new Error(`a line is tagged "${key}", which the release's modules: does not name`);
    }
    const text = tagged ? tagged[2]! : item[1]!;
    (notes[section] ??= []).push(text);
    items.push({ section, text, modules: lineModules });
  }
  if (Object.keys(notes).length === 0) throw new Error('release notes carry no notes — at least one "- " line under a section');
  return { version, kind: kind as ReleaseKind, scope, festivalId, date, modules, notes, items };
}

/* --------------------------------------------------- what a change touches */

/**
 * Where each module's own code lives (path prefixes). Only what clearly
 * belongs to ONE module is listed; shared plumbing (the shell, the bundle,
 * the admin's frame) belongs to the platform version. Tests are never a change
 * to a module.
 */
export const MODULE_HOMES: Readonly<Record<string, readonly string[]>> = {
  lineup: ['frontend/src/visitor/Schedule.', 'frontend/src/visitor/ActSheet.', 'frontend/src/visitor/ActLine.', 'frontend/src/admin/rave/RaveLineup.', 'frontend/src/admin/rave/ActEditor.'],
  schedule: ['frontend/src/visitor/Timetable.', 'frontend/src/visitor/TimetableGrid.'],
  search: ['backend/src/modules/search/', 'frontend/src/visitor/SearchAll.', 'shared/src/search.'],
  'my-lineup': ['frontend/src/visitor/MyLineup.', 'frontend/src/visitor/PlanGrid.', 'shared/src/plan.', 'shared/src/conflicts.', 'shared/src/ics.'],
  live: ['frontend/src/visitor/Live.', 'shared/src/live.'],
  map: ['frontend/src/visitor/Map.', 'frontend/src/admin/rave/RaveMap.', 'backend/src/modules/admin/map.', 'shared/src/geo.'],
  countdown: ['frontend/src/visitor/Countdown.'],
  info: ['frontend/src/visitor/Info.', 'frontend/src/admin/rave/RaveInfo.'],
  announcements: ['frontend/src/visitor/Announced.', 'frontend/src/visitor/UrgentBanner.', 'frontend/src/admin/rave/RaveAnnouncements.'],
  sponsors: ['frontend/src/admin/rave/RaveSponsors.'],
  notifications: ['backend/src/modules/notifications/', 'frontend/src/visitor/PushToggle.'],
  offline: ['frontend/src/offline/'],
  travel: ['backend/src/modules/travel/', 'frontend/src/visitor/rideboard/'],
  dating: ['backend/src/modules/dating/', 'frontend/src/visitor/Dating.'],
  photos: ['backend/src/modules/photos/', 'frontend/src/visitor/Photos.', 'frontend/src/admin/rave/RavePhotographers.', 'frontend/src/admin/rave/RaveGallery.'],
  'crowd-stats': [],
  'ai-assistant': ['backend/src/modules/ai/', 'shared/src/ai.', 'backend/src/lib/claude.', 'frontend/src/admin/ai/'],
  recordings: ['backend/src/modules/recordings/', 'shared/src/recordings.', 'frontend/src/visitor/FullSets.'],
  weather: ['backend/src/modules/weather/', 'shared/src/weather.', 'frontend/src/visitor/Weather.', 'frontend/src/visitor/raveWeather.'],
};

/** The modules whose own (non-test) files a change touches. */
export function modulesTouched(paths: readonly string[]): string[] {
  const out = new Set<string>();
  for (const p of paths) {
    if (/\.test\.[a-z]+$/.test(p)) continue;
    for (const [key, homes] of Object.entries(MODULE_HOMES)) if (homes.some((h) => p.startsWith(h))) out.add(key);
  }
  return [...out];
}

export interface ReleaseCheckInput {
  baseVersion: string;
  headVersion: string;
  /** `releases/<version>.md` texts at the head, by version. */
  notes: Readonly<Record<string, string>>;
  /** `MODULE_VERSIONS` at the base — null when the base predates them. */
  baseModules: Readonly<Record<string, string>> | null;
  headModules: Readonly<Record<string, string>>;
  changedPaths: readonly string[];
}

/**
 * What CI refuses on a PR into `main` (step 54). Every merge into `main`
 * deploys, and every deploy is a release with a number of its own — so the
 * platform version must go UP, its notes must exist and parse, and a module
 * whose code changed must have its own version bumped. Empty means pass.
 */
export function checkRelease(input: ReleaseCheckInput): string[] {
  const errors: string[] = [];
  const { baseVersion, headVersion } = input;
  if (!SEMVER.test(headVersion) || !SEMVER.test(baseVersion) || compareSemver(headVersion, baseVersion) <= 0) {
    errors.push(`bump the platform version in package.json: ${headVersion} is not above main's ${baseVersion} — every merge into main is a release`);
    return errors;
  }
  const text = input.notes[headVersion];
  if (text === undefined) {
    errors.push(`releases/${headVersion}.md is missing — write the release notes for ${headVersion}`);
  } else {
    try {
      const parsed = parseReleaseNotes(text);
      if (parsed.version !== headVersion) errors.push(`releases/${headVersion}.md says ${parsed.version}`);
    } catch (error) {
      errors.push(`releases/${headVersion}.md: ${(error as Error).message}`);
    }
  }
  if (input.baseModules) {
    for (const key of modulesTouched(input.changedPaths)) {
      const before = input.baseModules[key];
      const after = input.headModules[key];
      if (before !== undefined && after !== undefined && compareSemver(after, before) <= 0) {
        errors.push(`module "${key}" changed but its version did not — bump it in shared/src/release.ts (${before})`);
      }
    }
  }
  return errors;
}

/**
 * The row a successful deploy writes with `POST /admin/platform/releases`.
 * `previousVersion` is the highest released version below this one (from the
 * `releases/` files); `modules` is the version of each module the notes name.
 */
export function releaseRecord(text: string, ctx: {
  commitSha: string; migration: string | null; knownVersions: readonly string[];
  modules: Readonly<Record<string, string>>; createdBy: string | null; approvedBy: string | null;
}) {
  const n = parseReleaseNotes(text);
  const previousVersion = ctx.knownVersions
    .filter((v) => SEMVER.test(v) && compareSemver(v, n.version) < 0)
    .sort(compareSemver).at(-1) ?? null;
  return {
    component: 'platform', version: n.version, previousVersion, kind: n.kind, scope: n.scope, festivalId: n.festivalId,
    commitSha: ctx.commitSha, migration: ctx.migration, notes: n.notes, dependencies: {},
    modules: Object.fromEntries(n.modules.map((k) => [k, ctx.modules[k] ?? MODULE_VERSIONS[k]!])),
    createdBy: ctx.createdBy, approvedBy: ctx.approvedBy,
  };
}

/* ------------------------------------------------------ cutting a release */

/** The version after `v`: a PATCH fixes, a MINOR adds, a MAJOR breaks something. */
export function nextVersion(v: string, kind: 'patch' | 'minor' | 'major'): string {
  const [major, minor, patch] = v.split('.').map(Number) as [number, number, number];
  if (kind === 'major') return `${major + 1}.0.0`;
  if (kind === 'minor') return `${major}.${minor + 1}.0`;
  return `${major}.${minor}.${patch + 1}`;
}

/**
 * The source of `MODULE_VERSIONS` with each of `keys` set to `version` —
 * `scripts/release.mts next` writes it back, so a module's line is bumped by
 * the release that changed it, never by hand.
 */
export function bumpModuleVersions(source: string, keys: readonly string[], version: string): string {
  let out = source;
  for (const key of keys) {
    const name = key.includes('-') ? `'${key}'` : key;
    out = out.replace(new RegExp(`^(\\s+${name.replace(/[-']/g, (c) => `\\${c}`)}:\\s*)'[^']+'`, 'm'), `$1'${version}'`);
  }
  return out;
}
